import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { billingExplanation } from "../copy/companyBrainCopy";
import { reviewCreditCopy } from "../copy/reviewCreditCopy";
import { deriveHealthModel } from "./setupState";
import GitHubConnectionCard from "./GitHubConnectionCard";
import RepositoryConnectPanel from "./RepositoryConnectPanel";
import ReviewCreditPurchase from "./ReviewCreditPurchase";
import InlineAlert from "../ui/InlineAlert";
import Skeleton from "../ui/Skeleton";
import "./workspaceOverview.css";

const EXPERIMENTAL = process.env.REACT_APP_EXPERIMENTAL_TASKS === "true";

export default function SettingsPage({ canConnect = false, repositories = [], onRepositoryAdded }) {
  const [setup, setSetup] = useState(null);
  const [credits, setCredits] = useState(null);
  const [packages, setPackages] = useState([]);
  const [error, setError] = useState("");
  const [billingNotice, setBillingNotice] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/workspace/setup").then((response) => {
      if (!cancelled) setSetup(response.data || {});
    }).catch(() => {
      if (!cancelled) setError("Workspace settings could not be loaded.");
    });
    const creditsRequest = api.get("/billing/review-credits");
    if (creditsRequest && typeof creditsRequest.then === "function") {
      creditsRequest.then((response) => {
        if (!cancelled) setCredits(response.data || null);
      }).catch(() => {
        if (!cancelled) setCredits(null);
      });
    }
    const packagesRequest = api.get("/billing/review-credit-packages");
    if (packagesRequest && typeof packagesRequest.then === "function") {
      packagesRequest.then((response) => {
        if (!cancelled) setPackages(response.data?.packages || []);
      }).catch(() => {
        if (!cancelled) setPackages([]);
      });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    document.getElementById(id)?.scrollIntoView();
  }, [setup]);

  useEffect(() => {
    const billing = new URLSearchParams(window.location.search).get("billing");
    if (billing === "success") setBillingNotice("Payment received. Review credits appear after Stripe confirms the payment.");
    if (billing === "cancelled") setBillingNotice("Checkout was cancelled. No charge was made.");
  }, []);

  if (!setup && !error) return <Skeleton label="Loading settings" />;
  const health = deriveHealthModel(setup || {}, { canConnect });

  return (
    <div className="rr-stack rr-health">
      <header className="rr-page-header">
        <h1>Settings</h1>
        <p>Codebases, GitHub, billing, and account. Reviews do not wait on this page.</p>
      </header>
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
      <nav className="rr-settings-tabs" aria-label="Settings sections">
        <a href="#repositories">Codebases</a>
        <a href="#github">GitHub</a>
        <a href="#reviews">Review behavior</a>
        <a href="#billing">Billing</a>
        <a href="#account">Account</a>
      </nav>
      <section id="repositories">
        <RepositoryConnectPanel
          setup={setup}
          repositories={repositories}
          canConnect={canConnect}
          initiallyOpen={repositories.length === 0}
          onConnected={onRepositoryAdded}
        />
        <p><Link to="/brain">Open Company Brain</Link></p>
      </section>
      <section id="github">
        <GitHubConnectionCard setup={setup} canConnect={canConnect} />
        <p>RenderRepo reads repository contents and pull requests, and writes a Check when publication is enabled. It does not need repository administration.</p>
      </section>
      <section id="billing">
        <h2>Billing</h2>
        {billingNotice ? <InlineAlert>{billingNotice}</InlineAlert> : null}
        <p>{credits?.billingEnabled ? `${credits.available} reviews remaining` : billingExplanation()}</p>
        {credits?.purchasesEnabled ? (
          <>
            <p>
              {credits.reserved > 0 ? `${credits.reserved} reviews reserved. ` : ""}
              One completed review costs $1.
            </p>
            <ReviewCreditPurchase packages={packages} canPurchase={Boolean(credits?.canPurchase)} purchasesEnabled />
          </>
        ) : <p>{reviewCreditCopy}</p>}
      </section>
      <section id="reviews">
        <h2>Review settings</h2>
        <p>A review uses the current pull request and any Company Brain context already available. Background learning does not have to finish first.</p>
      </section>
      <section id="account">
        <h2>Account</h2>
        <ul className="rr-notes">
          {health.notes.map((note) => <li key={note}>{note}</li>)}
        </ul>
      </section>
      {EXPERIMENTAL ? (
        <section id="experimental">
          <h2>Experimental features</h2>
          <p><Link to="/tasks">Tasks</Link></p>
          <p><Link to="/tools">Repository tools</Link></p>
        </section>
      ) : null}
    </div>
  );
}
